import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';

export default function SignInForm() {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#FEF9F2',
            padding: '1rem',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        },
        card: {
            width: '100%',
            maxWidth: '28rem',
            backgroundColor: 'white',
            borderRadius: '1rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: '2rem'
        },
        header: {
            textAlign: 'center',
            marginBottom: '2rem'
        },
        iconContainer: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '4rem',
            height: '4rem',
            backgroundColor: '#E0E7FF',
            borderRadius: '50%',
            marginBottom: '1rem'
        },
        title: {
            fontSize: '1.875rem',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '0.5rem',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex'
        },
        subtitle: {
            color: '#6B7280',
            fontSize: '0.875rem'
        },
        alertSuccess: {
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#F0FDF4',
            border: '1px solid #86EFAC',
            borderRadius: '0.5rem',
            color: '#166534',
            fontSize: '0.875rem',
            fontWeight: '500'
        },
        alertError: {
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'flex-start'
        },
        errorText: {
            color: '#991B1B',
            fontSize: '0.875rem'
        },
        formGroup: {
            marginBottom: '1.25rem'
        },
        label: {
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '0.5rem'
        },
        inputWrapper: {
            position: 'relative'
        },
        inputIcon: {
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none'
        },
        input: {
            display: 'block',
            width: '100%',
            padding: '0.75rem 0.75rem 0.75rem 2.5rem',
            border: '1px solid #D1D5DB',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            transition: 'all 0.2s',
            boxSizing: 'border-box'
        },
        inputError: {
            borderColor: '#FCA5A5'
        },
        passwordInput: {
            paddingRight: '3rem'
        },
        eyeButton: {
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.25rem'
        },
        errorMessage: {
            marginTop: '0.25rem',
            fontSize: '0.875rem',
            color: '#DC2626'
        },
        rememberRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.875rem',
            marginBottom: '1.25rem',
            flexWrap: 'wrap',
            gap: '0.5rem'
        },
        checkbox: {
            display: 'flex',
            alignItems: 'center'
        },
        checkboxInput: {
            width: '1rem',
            height: '1rem',
            borderRadius: '0.25rem',
            border: '1px solid #D1D5DB',
            marginRight: '0.5rem',
            cursor: 'pointer'
        },
        link: {
            color: '#ff3700',
            fontWeight: '500',
            textDecoration: 'none',
            cursor: 'pointer'
        },
        button: {
            width: '100%',
            backgroundColor: '#ff3700',
            color: 'white',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            fontWeight: '500',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        buttonDisabled: {
            opacity: '0.5',
            cursor: 'not-allowed'
        },
        signupText: {
            marginTop: '1.5rem',
            textAlign: 'center',
            fontSize: '0.875rem',
            color: '#6B7280'
        },
        spinner: {
            border: '3px solid rgba(255, 255, 255, 0.3)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            width: '1.25rem',
            height: '1.25rem',
            animation: 'spin 0.8s linear infinite',
            marginRight: '0.75rem'
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');
        setSuccess(false);

        if (!validateForm()) return;

        setLoading(true);

        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
            // console.log(response)
            if (!response.ok) {
                setApiError('Login failed. Please try again.');
                return;
            }
            const userData = await response.json();

            // ✅ Save user data in localStorage
            localStorage.setItem('user', JSON.stringify(userData));

            // ✅ Optionally, if you only want specific fields:
            // localStorage.setItem('user', JSON.stringify({
            //   name: userData.name,
            //   email: userData.email,
            //   role: userData.role,
            //   permissions: userData.permissions
            // }));

            //console.log('Login successful:', userData.email);

            // ✅ Redirect to admin dashboard
            //console.log('Login successful:', userData);

            // ✅ Smart redirect based on permissions
            const permissions = userData?.data?.user?.permissions || [];

            // Define your permission → route mapping
            const permissionRoutes = {
                dashboard: '/admin/dash',
                normalUserList: '/userList',
                events: '/settings',
                redownload: '/redownload',
                vipEvents: '/vipEvents',
                uniqueEvent: '/unique/settings',
                uniqueUser: '/unique/userList',
                scanner: '/scanner',
                vipScanner: '/vip/camera/scanner',
                vipUserList: '/vipUserList',
                screenScanner: '/vip/screen/scanner',
                usersScanner: '/user/scanner',
                mediaScanner:"/media_scanner",
                referralCode:"/referral/code"
            };

            // Find first route user is allowed to access
            const firstAllowed = permissions
                .map(p => permissionRoutes[p])
                .find(route => route); // first non-undefined match

            if (firstAllowed) {
                navigate(firstAllowed);
            } else {
                // fallback if user has no known permissions
                setApiError('No permissions assigned. Please contact admin.');
            }
        } catch (error) {
            setApiError('Login failed. Please try again.');
            console.error('Login error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    return (
        <>
            <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        input:focus {
          outline: none !important;
          border-color: #6366F1 !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
        }
        button:hover:not(:disabled) {
          background-color: #4338CA !important;
        }
      `}</style>

            <div style={styles.container}>
                <Header />
                <div style={styles.card}>
                    <div style={styles.header}>
                        <div style={styles.iconContainer}>
                            <svg style={{ width: '2rem', height: '2rem', color: '#ff3700' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        {/* <h1 style={styles.title}>Welcome Back</h1> */}
                        <p style={styles.subtitle}>Sign in to your account</p>
                    </div>

                    {success && (
                        <div style={styles.alertSuccess}>
                            ✓ Login successful! Redirecting...
                        </div>
                    )}

                    {apiError && (
                        <div style={styles.alertError}>
                            <svg style={{ width: '1.25rem', height: '1.25rem', color: '#DC2626', marginRight: '0.5rem', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p style={styles.errorText}>{apiError}</p>
                        </div>
                    )}

                    <div>
                        <div style={styles.formGroup}>
                            <label htmlFor="email" style={styles.label}>Email Address</label>
                            <div style={styles.inputWrapper}>
                                <div style={styles.inputIcon}>
                                    <svg style={{ width: '1.25rem', height: '1.25rem', color: '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    style={{ ...styles.input, ...(errors.email ? styles.inputError : {}) }}
                                    placeholder="you@example.com"
                                />
                            </div>
                            {errors.email && <p style={styles.errorMessage}>{errors.email}</p>}
                        </div>

                        <div style={styles.formGroup}>
                            <label htmlFor="password" style={styles.label}>Password</label>
                            <div style={styles.inputWrapper}>
                                <div style={styles.inputIcon}>
                                    <svg style={{ width: '1.25rem', height: '1.25rem', color: '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                                    style={{ ...styles.input, ...styles.passwordInput, ...(errors.password ? styles.inputError : {}) }}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={styles.eyeButton}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? (
                                        <svg style={{ width: '1.25rem', height: '1.25rem', color: '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.88 3.88m0 0L9.88 9.88m-3-3L3 3m18 18l-3.88-3.88M17.121 14.121l-2.243-2.242m0 0L12 9m2.878 2.879l2.243 2.242" />
                                        </svg>
                                    ) : (
                                        <svg style={{ width: '1.25rem', height: '1.25rem', color: '#9CA3AF' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.password && <p style={styles.errorMessage}>{errors.password}</p>}
                        </div>

                        <div style={styles.rememberRow}>
                            <label style={styles.checkbox}>
                                <input type="checkbox" style={styles.checkboxInput} />
                                <span style={{ color: '#374151' }}>Remember me</span>
                            </label>
                            {/* <a href="#" style={styles.link} onClick={(e) => e.preventDefault()}>Forgot password?</a> */}
                        </div>

                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading}
                            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
                        >
                            {loading && <div style={styles.spinner}></div>}
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </div>


                </div>
            </div>
        </>
    );
}